
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { isFirstSession, claimSessionPrompt } from '@/utils/firstRun';


export const useSkillRatingCheck = () => {
  const { profile, user } = useAuth();
  const [showSkillRatingPrompt, setShowSkillRatingPrompt] = useState(false);
  const [hasChecked, setHasChecked] = useState(false);
  const [sessionDismissed, setSessionDismissed] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const checkSkillRating = useCallback(() => {
    try {
      // Don't check if already dismissed this session
      if (sessionDismissed || !user || !profile) {
        return;
      }

      // Check if skill rating needs to be defined using the new assessment system
      // More robust check: assessment is required if explicitly marked OR missing critical data
      const needsSkillRating = profile.skill_assessment_required === true || 
                                profile.skill_rating_defined !== true || 
                                !profile.skill_assessment_date || 
                                !profile.skill_assessment_data;
      
      // Never interrupt a user's very first session, and never stack the
      // assessment on top of another first-run prompt.
      if (needsSkillRating && !hasChecked && !isFirstSession() && claimSessionPrompt('skill')) {
        // Small delay to ensure smooth UI transition
        setTimeout(() => {
          setShowSkillRatingPrompt(true);
        }, 1000);
        setHasChecked(true);
      } else if (needsSkillRating && !hasChecked) {
        setHasChecked(true);
      } else if (!needsSkillRating && showSkillRatingPrompt) {
        // If the profile was updated and no longer needs skill rating, hide the prompt
        setShowSkillRatingPrompt(false);
      }
    } catch (err) {
      console.error('❌ useSkillRatingCheck: Error checking skill rating:', err);
      setError(err instanceof Error ? err : new Error('Failed to check skill rating'));
    }
  }, [user, profile, hasChecked, sessionDismissed, showSkillRatingPrompt]);

  useEffect(() => {
    try {
      checkSkillRating();
    } catch (err) {
      console.error('❌ useSkillRatingCheck: Error in useEffect:', err);
      setError(err instanceof Error ? err : new Error('Failed to initialize skill rating check'));
    }
  }, [checkSkillRating]);

  const dismissPrompt = useCallback(() => {
    try {
      setShowSkillRatingPrompt(false);
      setSessionDismissed(true);
      setError(null);
    } catch (err) {
      console.error('❌ useSkillRatingCheck: Error dismissing prompt:', err);
      setError(err instanceof Error ? err : new Error('Failed to dismiss prompt'));
    }
  }, []);

  // Reset session dismissal when user changes (e.g., logout/login)
  useEffect(() => {
    try {
      setSessionDismissed(false);
      setHasChecked(false);
      setError(null);
    } catch (err) {
      console.error('❌ useSkillRatingCheck: Error resetting state:', err);
      setError(err instanceof Error ? err : new Error('Failed to reset state'));
    }
  }, [user?.id]);

  return {
    showSkillRatingPrompt,
    dismissPrompt,
    error
  };
};
